import type { RouteLocationNormalizedGeneric } from "vue-router";

export type LfcLoginCookie = Partial<{
  id: string;
  is_active: string;
  username: string;
  nickname: string;
  forename: string;
  gender: string;
  dob: string;
  scriptdob: string;
  user_groups: string[]; // Assuming user_groups is an array of strings
  auth_string: string;
  accepted_terms: string;
  ip_addr: string;
  country_code: string;
  ugc_available: boolean;
  email_validated: boolean;
  logged_in_date: number;
  remember_me: boolean;
  refresh_date: number;
  check_date: number;
}>;

export const getIsHostLiverpool = () => {
  const host = discoverHost();
  const {
    public: { liverpoolFcHost },
  } = useRuntimeConfig();

  if (!liverpoolFcHost) return false;

  return host.includes(liverpoolFcHost);
};

export const getLiverpoolAuthLink = (params: {
  type: "login" | "join" | "forgot-password";
  redirectUrl: string;
}) => {
  const {
    public: { liverpoolFcHost },
  } = useRuntimeConfig();

  return `https://account.${liverpoolFcHost}/${params.type}?redirect_uri=${params.redirectUrl}`;
};

export const getRedirectToLiverpoolAuthMiddleware =
  (type: "login" | "forgot-password" | "join") =>
  (to: RouteLocationNormalizedGeneric) => {
    if (!getIsHostLiverpool()) {
      return;
    }

    const internalRedirect = to.query.redirect?.toString() ?? "";

    const liverpoolLoginLink = getLiverpoolAuthLink({
      type,
      redirectUrl: new URL(internalRedirect, window.location.origin).toString(),
    });

    return navigateTo(liverpoolLoginLink, {
      external: true,
    });
  };

const LFC_BEARER_TOKEN = "a4a641f6-8269-4a5b-ac8c-3d2d80e61111";

export const getIsUserAuthenticatedOnLiverpool = async () => {
  if (import.meta.dev) {
    // @todo-liverpool lfc hasn't whitelisted our local development domain (yet) so we can't make the request
    return Promise.resolve(true);
  }

  const {
    public: { liverpoolFcHost },
  } = useRuntimeConfig();

  try {
    // using fetch instead of the global axios to keep the request config for liverpool as barebones as possible
    const response = await fetch(
      `https://account.${liverpoolFcHost}/api/authenticated`,
      {
        credentials: "include",
        headers: { Authorization: `Bearer ${LFC_BEARER_TOKEN}` },
      },
    );

    const jsonifiedResponse = await response.json();

    return (
      !!jsonifiedResponse &&
      typeof jsonifiedResponse === "object" &&
      "success" in jsonifiedResponse &&
      (jsonifiedResponse.success as boolean)
    );
  } catch {
    return false;
  }
};

export const logoutFromLiverpool = async () => {
  if (import.meta.dev) {
    // @todo-liverpool lfc hasn't whitelisted our local development domain (yet) so we can't make the request
    return Promise.resolve();
  }

  const {
    public: { liverpoolFcHost },
  } = useRuntimeConfig();

  try {
    await fetch(`https://account.${liverpoolFcHost}/api/sign_out`, {
      method: "POST",
      credentials: "include",
      headers: { Authorization: `Bearer ${LFC_BEARER_TOKEN}` },
    });
  } catch {
    // fail silently as it's an external service
  }
};
